/* eslint-disable jsx-a11y/label-has-associated-control */
import React, {FC, useRef, useState} from 'react';
import ReactCrop, {Crop, PixelCrop} from 'react-image-crop';
import {DeleteOutlined, PlusOutlined} from '@ant-design/icons';
import {Image, Modal} from 'antd';
import {canvasPreview} from '../Image-cropper/canvasPreview';
import {useDebounceEffect, useModalState} from '@/hooks';
import 'react-image-crop/dist/ReactCrop.css';
import {centerAspectCrop} from '@/utils';
import styles from '../Image-cropper/index.module.scss';
import avatarStyles from './index.module.scss';

export interface AvatarCropperProps {
  handleChangeImage: (image: string) => void;
  handleChangeCroppedImage: (image: string) => void;
  image: string;
  croppedImage: string;
}

const AvatarCropper: FC<AvatarCropperProps> = ({
                                                 handleChangeImage,
                                                 image,
                                                 croppedImage,
                                                 handleChangeCroppedImage,
                                               }) => {
  const imgRef = useRef<HTMLImageElement>(null);
  const {isOpen, closeModal, openModal} = useModalState();
  const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [crop, setCrop] = useState<Crop | undefined>();

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      setCrop(undefined); // Makes crop preview update between images.
      const reader = new FileReader();
      reader.addEventListener('load', () => {
        if (reader.result !== null) {
          const selectedImageSrc = reader.result.toString() ?? '';
          setImgSrc(selectedImageSrc);
          handleChangeImage(selectedImageSrc);
        }
      });
      reader.readAsDataURL(e.target.files[0]);
      openModal();
    }
  };
  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const {width, height} = e.currentTarget;
    setCrop(centerAspectCrop(width, height, 1 / 1));
  };

  useDebounceEffect(
    async () => {
      if (completedCrop?.width && completedCrop?.height && imgRef.current) {
        // We use canvasPreview as it's much faster than imgPreview.
        const croppedImage = await canvasPreview(imgRef.current, completedCrop);
        if (croppedImage) {
          handleChangeCroppedImage(croppedImage);
        }
      }
    },
    100,
    [completedCrop]
  );

  const onDeleteImage = () => {
    handleChangeImage(undefined);
    handleChangeCroppedImage(undefined);
  };

  return (
    <div>
      {!image && (
        <div className={styles.cropControls}>
          <div className={styles.inputContainer}>
            <input
              className={styles.uploadInput}
              type='file'
              accept='image/*'
              onChange={onSelectFile}
            />
            <label className={styles.uploadBtn} htmlFor='uploadInput'>
              <PlusOutlined/>
              <span>Upload</span>
            </label>
          </div>
        </div>
      )}

      <Modal
        title={
          <div className={styles.titleWrap}>
            <span>Crop Image</span>
          </div>
        }
        centered
        open={isOpen}
        onOk={closeModal}
        onCancel={() => {
          onDeleteImage();
          closeModal();
        }}
      >
        <div className={styles.cropperWrapper}>
          {Boolean(imgSrc) && (
            <ReactCrop
              ruleOfThirds
              className={styles.cropper}
              crop={crop}
              onChange={(_, percentCrop) => setCrop(percentCrop)}
              onComplete={c => setCompletedCrop(c)}
              aspect={1 / 1}
            >
              <img
                className={styles.cropperImage}
                ref={imgRef}
                alt='Crop me'
                src={imgSrc}
                onLoad={onImageLoad}
              />
            </ReactCrop>
          )}
        </div>

        <div className={styles.croppedImageWrap}>
          <label htmlFor='croppedImage'>
            <span className={styles.croppedImageDesc}>
              Cropped Image <small>(Click to preview)</small> :
            </span>

            <Image
              id='croppedImage'
              width={100}
              height={100}
              src={croppedImage}
            />
          </label>
        </div>
      </Modal>

      {croppedImage && (
        <div className={avatarStyles.avatarImageWrap}>
          <Image id='avatarImage' width={100} height={100} src={croppedImage}/>
          <button
            onClick={onDeleteImage}
            className={styles.removeBtn}
            type='button'
          >
            <DeleteOutlined/>
          </button>
        </div>
      )}
    </div>
  );
};
export default AvatarCropper;
